import React from 'react';
import { Link } from 'react-router-dom';
import type { LabeledTableViz } from 'shared/src/api/ai-labeled-viz-types';
import type { CubeRow, RawColumn, CellFormat } from 'shared/src/utils/ai/ai-viz-transformers';
import type { EntityLinks } from 'shared/src/trpc-types/cube-load-trpc-types';
import { transformTable, formatVizValue } from 'shared/src/utils/ai/ai-viz-transformers';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/forms/ui/tooltip';

type TableColumn = { key: string; label: string; format?: CellFormat };

type DataTableProps = {
  rows: CubeRow[];
  viz: LabeledTableViz;
  links?: EntityLinks;
};

type RawDataTableProps = {
  rows: CubeRow[];
  columns: RawColumn[];
  links?: EntityLinks;
};

const TABLE_ROW_CAP = 100;

export function DataTable({ rows, viz, links }: DataTableProps) {
  const { columns } = transformTable(rows, viz);

  if (rows.length === 0) {
    return (
      <div className="w-full rounded-2xl bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-2xl font-semibold">{viz.title}</h2>
        <div className="flex items-center justify-center" style={{ height: 200 }}>
          <p className="text-lg text-gray-400">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-2xl bg-white p-6 shadow-lg">
      <h2 className="mb-4 text-2xl font-semibold">{viz.title}</h2>
      <CappedTable
        columns={columns}
        rows={rows}
        links={links}
        scrollClassName="max-h-[50vh] overflow-auto"
        borderClassName="border-t border-gray-200 px-3 py-2"
        buttonClassName="text-xs text-gray-500 hover:text-gray-700"
      />
    </div>
  );
}

export function RawDataTable({ rows, columns, links }: RawDataTableProps) {
  if (rows.length === 0) {
    return <p className="px-3 py-2 text-xs text-muted-foreground">No rows</p>;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border/50 bg-muted/30">
      <CappedTable
        columns={columns}
        rows={rows}
        links={links}
        scrollClassName="max-h-72 overflow-auto"
        borderClassName="border-t border-border/50 px-3 py-2"
        buttonClassName="text-xs text-muted-foreground hover:text-foreground"
      />
    </div>
  );
}

type CappedTableProps = {
  columns: TableColumn[];
  rows: CubeRow[];
  links?: EntityLinks;
  scrollClassName: string;
  borderClassName: string;
  buttonClassName: string;
};

function CappedTable({
  columns,
  rows,
  links,
  scrollClassName,
  borderClassName,
  buttonClassName
}: CappedTableProps) {
  const [showAll, setShowAll] = React.useState(false);
  const capped = !showAll && rows.length > TABLE_ROW_CAP;
  const visibleRows = capped ? rows.slice(0, TABLE_ROW_CAP) : rows;

  return (
    <>
      <div className={scrollClassName}>
        <InnerTable columns={columns} rows={visibleRows} links={links} />
      </div>
      {capped ? (
        <div className={borderClassName}>
          <button type="button" onClick={() => setShowAll(true)} className={buttonClassName}>
            Show all {rows.length} rows
          </button>
        </div>
      ) : null}
    </>
  );
}

type InnerTableProps = {
  columns: TableColumn[];
  rows: CubeRow[];
  links?: EntityLinks;
};

function InnerTable({ columns, rows, links }: InnerTableProps) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-gray-200 text-left">
          {columns.map(col => (
            <th
              key={col.key}
              className="sticky top-0 max-w-96 truncate bg-white px-3 py-2 font-medium text-gray-600">
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 1 ? 'bg-gray-50' : undefined}>
            {columns.map(col => {
              const entry = links?.[col.key];
              return (
                <td key={col.key} className="max-w-96 truncate px-3 py-2 tabular-nums">
                  {entry?.isArray ? (
                    <ArrayLinkedCell value={row[col.key]} urls={entry.urls} />
                  ) : (
                    <LinkedCell
                      value={row[col.key]}
                      format={col.format ?? ('string' as CellFormat)}
                      href={resolveLink(links, col.key, row)}
                    />
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LinkedCell({
  value,
  format,
  href
}: {
  value: unknown;
  format: CellFormat;
  href?: string;
}) {
  const formatted = formatVizValue(value, format);
  if (!href) return <>{formatted}</>;

  return (
    <Link
      to={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline decoration-blue-600/30 hover:decoration-blue-600">
      {formatted}
    </Link>
  );
}

function resolveLink(
  links: EntityLinks | undefined,
  colKey: string,
  row: Record<string, unknown>
): string | undefined {
  const entry = links?.[colKey];
  if (!entry) return undefined;
  const lookupValue = String(row[entry.lookupColumn ?? colKey] ?? '');
  return entry.urls[lookupValue];
}

const ARRAY_VISIBLE_LIMIT = 3;

function ArrayLinkedCell({ value, urls }: { value: unknown; urls: Record<string, string> }) {
  const items = parseJsonArray(value);
  if (items.length === 0) return <>--</>;

  const visible = items.slice(0, ARRAY_VISIBLE_LIMIT);
  const overflow = items.slice(ARRAY_VISIBLE_LIMIT);

  return (
    <span className="inline-flex items-baseline gap-x-1.5 whitespace-nowrap">
      {visible.map((item, i) => {
        const href = urls[item];
        return (
          <React.Fragment key={item}>
            {i > 0 ? <span className="text-gray-300">,</span> : null}
            {href ? (
              <Link
                to={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline decoration-blue-600/30 hover:decoration-blue-600">
                {item}
              </Link>
            ) : (
              <span className="text-gray-400">{item}</span>
            )}
          </React.Fragment>
        );
      })}
      {overflow.length > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default text-gray-400">+{overflow.length} more</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <span className="whitespace-pre-wrap">{overflow.join(', ')}</span>
          </TooltipContent>
        </Tooltip>
      ) : null}
    </span>
  );
}

function parseJsonArray(value: unknown): string[] {
  if (typeof value !== 'string') return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}
